import React from 'react';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import type { Payment, AffaireAllocation, AffairePaymentStatsEnhanced } from '../../types';

interface AcceptanceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: Payment;
  affaire?: AffaireAllocation;
  affairePaymentStats?: Record<string, AffairePaymentStatsEnhanced>;
  onRevert: (paymentId: number, affaireId: string) => Promise<void>;
}

export const AcceptanceDetailsModal: React.FC<AcceptanceDetailsModalProps> = ({
  isOpen,
  onClose,
  payment,
  affaire,
  affairePaymentStats,
  onRevert
}) => {
  if (!isOpen || !affaire) return null;

  // Check if this is a multi-payment affaire and get overall stats
  const stats = affaire?.affaire_id ? affairePaymentStats?.[affaire.affaire_id] : null;
  const isMultiPayment = stats && stats.paymentCount > 1 && stats.expectedAmount;

  const formatAmount = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('fr-BE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDifference = () => {
    if (isMultiPayment && stats && stats.expectedAmount !== null) {
      // Multi-payment: use overall stats
      return stats.totalAmount - stats.expectedAmount;
    }
    // Single payment: use per-payment values
    if (!affaire?.expected_amount) return 0;
    return affaire.amount_allocated - affaire.expected_amount;
  };

  // Get the amounts to display
  const receivedAmount = isMultiPayment && stats ? stats.totalAmount : affaire.amount_allocated;
  const expectedAmount = isMultiPayment && stats ? stats.expectedAmount : affaire?.expected_amount;

  const difference = calculateDifference();
  const differencePercentage = expectedAmount
    ? Math.abs((difference / expectedAmount) * 100).toFixed(1)
    : 0;

  const isOverpayment = difference > 0;
  const isUnderpayment = difference < 0;

  const handleRevert = async () => {
    try {
      await onRevert(payment.id, affaire.affaire_id);
      onClose();
    } catch (error) {
      console.error('Error reverting acceptance:', error);
      alert('Erreur lors de l\'annulation de l\'acceptation');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
          <div className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-gray-100">
                <InformationCircleIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">
                  Détails de l'acceptation
                </h3>
                
                <div className="mt-4 space-y-4">
                  {/* Transaction Info */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Information de transaction</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transaction:</span>
                        <span className="font-medium text-gray-900">{payment.transaction_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Affaire:</span>
                        <span className="font-medium text-gray-900">{affaire.affaire_id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Statut paiement:</span>
                        <span className="font-medium text-gray-900">{affaire.payment_status || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Amount Details */}
                  <div className={`rounded-lg p-3 ${
                    Math.abs(difference) < 0.01 ? 'bg-green-50' : 'bg-amber-50'
                  }`}>
                    <h4 className={`text-sm font-semibold mb-2 ${
                      Math.abs(difference) < 0.01 ? 'text-green-800' : 'text-amber-800'
                    }`}>
                      {isMultiPayment ? 'Montants globaux' : 'Différence acceptée'}
                    </h4>
                    <div className="text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Montant attendu:</span>
                        <span className="font-medium text-gray-900">
                          {formatAmount(expectedAmount || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {isMultiPayment ? `Total reçu (${stats?.paymentCount} paiements):` : 'Montant reçu:'}
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatAmount(receivedAmount)}
                        </span>
                      </div>
                      <div className={`flex justify-between pt-2 border-t ${
                        Math.abs(difference) < 0.01 ? 'border-green-200' : 'border-amber-200'
                      }`}>
                        <span className={`font-medium ${
                          Math.abs(difference) < 0.01 ? 'text-green-700' : 'text-amber-700'
                        }`}>
                          {isMultiPayment ? 'Différence globale:' : 'Différence:'}
                        </span>
                        <span className={`font-bold ${
                          Math.abs(difference) < 0.01 ? 'text-green-600' :
                          isOverpayment ? 'text-blue-600' : 'text-orange-600'
                        }`}>
                          {Math.abs(difference) < 0.01
                            ? '0,00 € (complet)'
                            : `${difference > 0 ? '+' : ''}${formatAmount(difference)} (${differencePercentage}%)`
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Acceptance Info */}
                  <div className="border rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Information d'acceptation</h4>
                    <div className="text-sm space-y-2">
                      {affaire.acceptance_date && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date d'acceptation:</span>
                          <span className="text-gray-900">
                            {formatDate(affaire.acceptance_date)}
                          </span>
                        </div>
                      )}
                      {affaire.accepted_by && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Accepté par:</span>
                          <span className="text-gray-900">{affaire.accepted_by}</span>
                        </div>
                      )}
                      {affaire.acceptance_note && (
                        <div className="mt-3">
                          <span className="text-gray-600 block mb-1">Note d'acceptation:</span>
                          <div className="bg-white p-2 rounded border border-gray-200 text-gray-800 italic">
                            "{affaire.acceptance_note}"
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="rounded-md bg-orange-50 p-3">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-orange-400 mr-2 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-orange-800">
                        <p className="font-medium mb-1">Annuler l'acceptation ?</p>
                        <p>Cette action supprimera l'acceptation et le paiement redeviendra non validé. 
                           La différence devra être traitée à nouveau.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={handleRevert}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
            >
              Annuler l'acceptation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};